import { useEffect, useState } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsCarBooking';

const EMPTY = {
  idEmpleado: 0,
  activo: true,   // 👈 se mantiene internamente, no se muestra
};

// Endpoints + modelos
const endpointEmpleado = ENDPOINTS.empleados;
const modeloEmpleado = Models.empleados;

const endpointPersona = ENDPOINTS.personas;
const modeloPersona = Models.persona;

const endpointUnidades = ENDPOINTS.unidadesOrg;
const modeloUnidad = Models.unidadesOrg;

export default function Modal({
  open,
  mode = 'new',
  data = null,
  onClose,
  onSubmit,
  inline = false,
}) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({
    idEmpleado: '',
  });

  // catálogos
  const [empleados, setEmpleados] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [loadingCatalogos, setLoadingCatalogos] = useState(false);
  const [errorCatalogos, setErrorCatalogos] = useState(null);

  const validateField = (field, rawValue) => {
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    let error = '';

    switch (field) {
      case 'idEmpleado':
        if (!value || Number(value) <= 0) error = 'Selecciona un empleado.';
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const validateAll = () => {
    const fields = ['idEmpleado'];
    let isValid = true;
    fields.forEach(f => {
      if (!validateField(f, form[f])) isValid = false;
    });
    return isValid;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  // Cargar datos de edición
  useEffect(() => {
    if (mode === 'edit' && data) {
      const filled = {
        idSolicitante: data.idSolicitante ?? 0,
        idEmpleado: data.idEmpleado ?? 0,
        activo: !!data.activo,
      };
      setForm(filled);
      validateField('idEmpleado', filled.idEmpleado);
    } else {
      setForm(EMPTY);
      setErrors({ idEmpleado: '' });
    }
  }, [mode, data]);

  // Cargar catálogos cuando se abre el modal
  useEffect(() => {
    if (!open) return;

    let canceled = false;

    async function loadCatalogos() {
      setLoadingCatalogos(true);
      setErrorCatalogos(null);

      try {
        const [resEmps, resPers, resUnis, resSolicitantes] = await Promise.all([
          axios.get(endpointEmpleado),
          axios.get(endpointPersona),
          axios.get(endpointUnidades),
          axios.get(ENDPOINTS.solicitantes),
        ]);

        const emps = (resEmps.data || []).map((x) => modeloEmpleado(x));
        const pers = (resPers.data || []).map((x) => modeloPersona(x));
        const unis = (resUnis.data || []).map((x) => modeloUnidad(x));

        // Filtrar empleados que ya son solicitantes (excepto el actual en edición)
        const solicitantesExistentes = resSolicitantes.data || [];
        const empleadosFiltrados = emps.filter(emp => {
          // En edición, permitir el empleado actual
          if (mode === 'edit' && data && emp.idEmpleado === data.idEmpleado) {
            return true;
          }
          // Excluir si ya existe en solicitantes
          return !solicitantesExistentes.some(s => s.idEmpleado === emp.idEmpleado);
        });

        if (!canceled) {
          setEmpleados(empleadosFiltrados);
          setPersonas(pers);
          setUnidades(unis);
        }
      } catch (err) {
        console.error('Error cargando catálogos de solicitantes', err);
        if (!canceled) {
          setErrorCatalogos('No se pudieron cargar los catálogos.');
        }
      } finally {
        if (!canceled) {
          setLoadingCatalogos(false);
        }
      }
    }

    loadCatalogos();

    return () => {
      canceled = true;
    };
  }, [open]);

  if (!open) return null;

  const hasErrors = Object.values(errors).some((e) => !!e);
  const isSaveDisabled = !form.idEmpleado || hasErrors;

  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '4px',
  };

  // Helpers de etiqueta
  function labelEmpleado(emp) {
    if (!emp) return '';
    const per = personas.find((p) => p.idPersona === emp.idPersona);
    const fullName = per
      ? `${per.nombre || ''} ${per.apellidoPaterno || ''} ${per.apellidoMaterno || ''}`.trim()
      : '';

    // Mostrar solo el nombre completo
    return fullName || `Empleado sin nombre`;
  }

  function labelUnidad(uni) {
    if (!uni) return '';
    const clave = uni.clave || '';
    const nombre = uni.nombre || uni.descripcion || '';
    return `${nombre} - (${clave})`;
  }

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}>
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nuevo solicitante' : 'Editar solicitante'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '12%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}>

      {/* Empleado */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Empleado:</label>
        <br /><br />
        <select
          className={`infoField${errors.idEmpleado ? ' infoField--error' : ''}`}
          value={form.idEmpleado || 0}
          onChange={(e) => handleChange('idEmpleado', Number(e.target.value) || 0)}
        >
          <option value={0} disabled>
            {loadingCatalogos ? 'Cargando empleados…' : 'Seleccione un empleado…'}
          </option>
          {empleados.map((emp) => (
            <option key={emp.idEmpleado} value={emp.idEmpleado}>
              {labelEmpleado(emp)}
            </option>
          ))}
        </select>
        {errors.idEmpleado && <small style={errorStyle}>{errors.idEmpleado}</small>}
      </div>



      {errorCatalogos && (
        <div style={{ gridColumn: '1 / -1' }}>
          <p
            style={{
              color: '#b91c1c',
              fontSize: '.8rem',
              marginTop: '.25rem',
            }}
          >
            {errorCatalogos}
          </p>
        </div>
      )}

      {/* 👇 Activo se mantiene solo en el form, no se muestra en UI */}
      {/*
      <div style={{ gridColumn: '1 / 3' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>
          <input
            type="checkbox"
            checked={form.activo}
            onChange={(e) => setForm({ ...form, activo: e.target.checked })}
            style={{ marginRight: '.5rem' }}
          />
          Activo
        </label>
      </div>
      */}
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="roles-btn" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={() => {
          if (validateAll()) {
            // Agregar idUnidadOrganizacional fijo como 1
            const payload = { ...form, idUnidadOrganizacional: 1 };
            onSubmit(payload, mode);
          }
        }}
        disabled={isSaveDisabled}
        style={{
          opacity: isSaveDisabled ? 0.5 : 1,
          cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        {mode === 'new' ? 'Guardar' : 'Actualizar'}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {Header}
        {Body}
        {Footer}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(900px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    </div>
  );
}
